import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Paper, Tabs, Tab, 
  TextField, Button, Switch, FormControlLabel, Alert,
  CircularProgress, Divider
} from '@mui/material';
import { 
  fetchAllMessageTemplates, 
  createMessageTemplate, 
  updateMessageTemplate,
  setActiveTemplate
} from '../../services/messageTemplates.service';
import OffHoursConfigPanel from './OffHoursConfigPanel';
import { useAuth } from '../../context/AuthContext';
import { LogOut, Clock, Menu, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const templateTypes = [
  { value: 'WELCOME_MESSAGE', label: 'Welcome Message', placeholders: ['{{name}}'] },
  { value: 'WELCOME_MESSAGE_OFF_HOURS', label: 'Welcome Message Off Hours', placeholders: ['{{name}}'] },
 /* { value: 'APPOINTMENT_REMINDER_SAME_DAY', label: 'Appointment Reminder Same Day', placeholders: ['{{name}}', '{{date}}', '{{time}}'] },
  { value: 'APPOINTMENT_REMINDER_24H', label: 'Appointment Reminder 24h Prior', placeholders: ['{{name}}', '{{date}}', '{{time}}'] },
  { value: 'APPOINTMENT_CONFIRMATION', label: 'Appointment Confirmation', placeholders: ['{{name}}', '{{date}}', '{{time}}'] }*/
];

/**
 * Component for managing message templates
 */
const MessageTemplatesPage = () => {
  const [templates, setTemplates] = useState({});
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentTemplate, setCurrentTemplate] = useState({
    content: '',
    isActive: true
  });
  const { currentUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  useEffect(() => {
    loadTemplates();
  }, []);
  
  /**
   * Load all message templates
   */
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const allTemplates = await fetchAllMessageTemplates();
      
      // Organize templates by type
      const templatesByType = {};
      templateTypes.forEach(type => {
        templatesByType[type.value] = allTemplates.filter(
          template => template.type === type.value
        );
      });
      
      setTemplates(templatesByType);
      
      // Set the current template to the active one for the current tab
      setCurrentTemplateForTab(currentTab, templatesByType);
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Set the current template based on the selected tab
   * @param {number} tabIndex - Tab index
   * @param {Object} templatesByType - Templates organized by type
   */
  const setCurrentTemplateForTab = (tabIndex, templatesByTypeObj = null) => {
    const templatesByType = templatesByTypeObj || templates;
    const type = templateTypes[tabIndex].value;
    const templatesOfType = templatesByType[type] || [];
    
    // Find active template or use the first one
    const activeTemplate = templatesOfType.find(t => t.isActive) || templatesOfType[0];
    
    if (activeTemplate) {
      setCurrentTemplate(activeTemplate);
    } else {
      // Create a new template if none exists
      setCurrentTemplate({
        type,
        content: getDefaultContent(type),
        isActive: true
      });
    }
  };
  
  /**
   * Get default content for a template type
   * @param {string} type - Template type
   * @returns {string} - Default content
   */
  const getDefaultContent = (type) => {
    switch (type) {
      case 'WELCOME_MESSAGE':
        return 'Welcome {{name}}! Thank you for contacting us. How can we assist you today?';
      case 'WELCOME_MESSAGE_OFF_HOURS':
        return 'Thank you for contacting us, {{name}}. Our office is currently closed. We will respond to your message during our regular business hours.';
    /*  case 'APPOINTMENT_REMINDER_SAME_DAY':
        return 'Reminder: You have an appointment today, {{date}} at {{time}}. We look forward to seeing you!';
      case 'APPOINTMENT_REMINDER_24H':
        return 'Reminder: You have an appointment tomorrow, {{date}} at {{time}}. Please let us know if you need to reschedule.';
      case 'APPOINTMENT_CONFIRMATION':
        return 'Your appointment has been confirmed for {{date}} at {{time}}. Thank you!';*/
      default:
        return '';
    }
  };
  
  /**
   * Handle tab change
   * @param {Event} event - Event object
   * @param {number} newValue - New tab index
   */
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setCurrentTemplateForTab(newValue);
  };
  
  /**
   * Handle template content change
   * @param {Event} event - Event object
   */
  const handleContentChange = (event) => {
    setCurrentTemplate({
      ...currentTemplate,
      content: event.target.value
    });
  };
  
  /**
   * Handle template active state change
   * @param {Event} event - Event object
   */
  const handleActiveChange = (event) => {
    setCurrentTemplate({
      ...currentTemplate,
      isActive: event.target.checked
    });
  };
  
  /**
   * Save the current template
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      
      const type = templateTypes[currentTab].value;
      let result;
      
      if (currentTemplate.id) {
        // Update existing template
        result = await updateMessageTemplate({
          id: currentTemplate.id,
          content: currentTemplate.content,
          isActive: currentTemplate.isActive
        });
      } else {
        // Create new template
        result = await createMessageTemplate({
          type,
          content: currentTemplate.content,
          isActive: true,
          createdBy: currentUser?.email || "admin"
        });
      }
      
      // If this template is active, deactivate other templates of the same type
      if (result.isActive) {
        await setActiveTemplate(result.id, type);
      }
      
      setMessage({ type: 'success', text: 'Template saved successfully' });
      
      // Reload templates to get the updated list
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Create a new template
   */
  const handleCreateNew = () => {
    const type = templateTypes[currentTab].value;
    setCurrentTemplate({
      type,
      content: getDefaultContent(type),
      isActive: false
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  const currentType = templateTypes[currentTab] || templateTypes[0];
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`bg-gray-900 text-white flex flex-col justify-between transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div>
          <div className="p-4 flex items-center justify-between">
            <h1 className="text-xl font-bold whitespace-nowrap overflow-hidden transition-all duration-300">
              {sidebarOpen ? 'Submission Hub' : 'SH'}
            </h1>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
              <Menu size={20} />
            </button>
          </div>
          <nav className="mt-4">
            <ul>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/manage-slots" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Slots</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/manage-appointments" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Appointments</span>}
                </Link>
              </li>            
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Submissions</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center bg-gray-800 cursor-pointer">
                <Link to="/message-templates" className="flex items-center w-full text-white">
                  <MessageSquare size={20} />
                  {sidebarOpen && <span className="ml-3">Message Templates</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/appointment-message-variants" className="flex items-center w-full text-white">
                  <MessageSquare size={20} />
                  {sidebarOpen && <span className="ml-3">Message Variants</span>}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        <div className="p-4">
          <div className="flex items-center cursor-pointer hover:bg-gray-800 p-2 rounded" onClick={handleLogout}>
            <LogOut size={20} />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm px-4 md:px-6 py-4 flex items-center justify-end">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
              {currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-gray-700">{currentUser?.email || 'User'}</p>
              <p className="text-xs text-gray-500">Admin</p>
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
          <div className="w-full">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Message Templates</h2>
            
            {message.text && (
              <Alert 
                severity={message.type} 
                onClose={() => setMessage({ type: '', text: '' })}
                className="mb-4"
              >
                {message.text}
              </Alert>
            )}
            
            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper elevation={2} className="p-4">
                <Tabs 
                  value={currentTab} 
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  className="mb-4"
                >
                  {templateTypes.map((type, index) => (
                    <Tab key={type.value} label={type.label} id={`template-tab-${index}`} />
                  ))}
                </Tabs>
                
                <Box mb={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentTemplate.isActive || false}
                        onChange={handleActiveChange}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Box>
                
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Template Content
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    variant="outlined"
                    value={currentTemplate.content || ''}
                    onChange={handleContentChange}
                  />                  
                </Box>                            
                
                <Box display="flex" justifyContent="space-between" mb={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || !currentTemplate.content}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Save Template'}
                  </Button>
                                    
                </Box>
                
                {/* Only show Off Hours Config when on the Welcome Message Off Hours tab */}
                {currentType.value === 'WELCOME_MESSAGE_OFF_HOURS' && (
                  <>
                    <Divider className="my-4" />
                    <Typography variant="h6" gutterBottom>
                      Off-Hours Configuration
                    </Typography>
                    <OffHoursConfigPanel />
                  </>
                )}
              </Paper>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MessageTemplatesPage;
